import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { isDev, DeviceOrientationData, watchDeviceOrientation } from 'utils';
import { CameraSettings } from 'components/pages/photoseries/store/root';
import { darkThemeBackground, lightThemeBackground } from 'hooks/useTheme/useTheme.util';

import { IDeviceInfo } from 'redux/session/session';
import { selectDeviceInfo } from 'redux/root.selectors';

const CameraLayoutWrapper = styled.div<{ shouldDisplay: boolean }>`
  display: ${({ shouldDisplay }) => (shouldDisplay ? 'block' : 'none')};
  position: absolute;
  z-index: 2;
  height: var(--view-height);
  width: calc(100vw - var(--toolbar-width));

  @media screen and (max-aspect-ratio: 13/9) {
    transform: rotate(90deg);
    transform-origin: left top;
    left: 100vw;
    min-height: 100vw;
    max-height: 100vw;
    min-width: calc(var(--view-height) - var(--toolbar-width));
    max-width: calc(var(--view-height) - var(--toolbar-width));
  }
`;

const Header = styled.h1<{ color: string, background: string }>`
  font-style: normal;
  font-weight: bold;
  font-size: 0.75rem;
  line-height: 200%;
  color: ${({ color }) => color};
  background: ${({ background }) => background};
  border-radius: 0rem 0rem 0.875rem 0.875rem;
  padding: 0.375rem 1.5625rem;
  width: fit-content;
  height: fit-content;
  margin: 0 auto;
`;

const Frame = styled.div<{ screenFeedWidth: number }>`
  background-color: transparent;
  border: 0.125rem solid #fff;
  border-radius: 0.75rem;
  width: ${({ screenFeedWidth }) => `calc(0.9 * ${screenFeedWidth}px);`}
  height: 80%;
  position: relative;
  z-index: 4;
  top: 0.688rem;
  left: 0;
  right: 0;
  margin: 0 auto;
`;

const Footer = styled.h2<{ color: string, background: string }>`
  font-style: normal;
  font-weight: bold;
  font-size: 0.5rem;
  line-height: 0.625rem;
  color: ${({ color }) => color};
  background: ${({ background }) => background};
  box-shadow: 0rem 0.0625rem 0.125rem rgba(0, 0, 0, 0.62);
  border-radius: 1.875rem;
  padding: 0.5rem 1.5625rem;
  width: fit-content;
  position: absolute;
  z-index: 4;
  bottom: -15px;
  left: 0;
  right: 0;
  margin: 0 auto;
  text-transform: uppercase;
`;

const CameraSettingsWrapper = styled.span`
  position: fixed;
  width: 8.5rem;
  height: 9rem;
  top: 0;
  left: 0;
  background-color: white;
  color: black;
  display: flex;
  flex-direction: column;
  font-size: 0.9rem;
  line-height: 1rem;
  padding: 0.5rem;

  b {
    font-weight: 600;
  }
`;

type CameraLayoutPropTypes = {
  headingText: string;
  footerText: string;
  cameraSettings: null | CameraSettings;
  screenSize: {
    width: number;
    height: number;
  };
  showCameraLayout: boolean;
  showCaptureFrame: boolean;
};

export const CameraLayout: React.FC<CameraLayoutPropTypes> = ({
  headingText, footerText, cameraSettings, screenSize, showCameraLayout, showCaptureFrame
}) => {
  let cameraAspectRatio;
  const { height: screenHeight } = screenSize;
  const [orientation, setOrientation] = useState<DeviceOrientationData | null>(null);

  const deviceInfo: IDeviceInfo = useSelector(selectDeviceInfo);

  if (cameraSettings) {
    const cameraFeedWidth = Math.max(cameraSettings.width, cameraSettings.height);
    const cameraFeedHeight = Math.min(cameraSettings.width, cameraSettings.height);

    cameraAspectRatio = cameraFeedWidth / cameraFeedHeight;
  }

  useEffect(() => {
    if (!isDev) return;
    const cleanup = watchDeviceOrientation((data) => setOrientation(data));

    // Cleanup when component unmounts
    return cleanup;
  }, []);

  return (
    <CameraLayoutWrapper shouldDisplay={showCameraLayout}>
      <Header background={lightThemeBackground.colorBackgroundBase} color={darkThemeBackground.colorBackgroundBase}>{headingText}</Header>
      {showCaptureFrame && cameraAspectRatio && (
        <Frame screenFeedWidth={Math.floor(cameraAspectRatio * screenHeight)}>
          <Footer background={lightThemeBackground.colorBackgroundBase} color={darkThemeBackground.colorBackgroundBase}>{footerText}</Footer>
        </Frame>
      )}
      {isDev && (
        <CameraSettingsWrapper>
          <div>
            <b>Height: </b><span>{cameraSettings?.height}</span>
          </div>
          <div>
            <b>Width: </b><span>{cameraSettings?.width}</span>
          </div>
          <div>
            <b>Ratio: </b><span>{cameraAspectRatio?.toPrecision(3)}</span>
          </div>
          <div>
            <b>Model: </b><span>{deviceInfo?.rawModel}</span>
          </div>
          <div>
            <b>X: </b><span>{orientation?.beta != null ? orientation.beta.toPrecision(2) : '-'}</span>
          </div>
          <div>
            <b>Y: </b><span>{orientation?.gamma != null ? orientation.gamma.toPrecision(2) : '-'}</span>
          </div>
          <div>
            <b>Z: </b><span>{orientation?.alpha != null ? orientation.alpha.toPrecision(2) : '-'}</span>
          </div>
        </CameraSettingsWrapper>)}
    </CameraLayoutWrapper>
  );
};
